import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import type { SxProps, Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fonts, radius } from '@/shared/theme';
import { EmptyState } from './empty-state';
import { Icon } from './icon';

export interface DataColumn<T> {
  key: string;
  header: ReactNode;
  /** Full name shown as tooltip (abbreviated KPI headers). */
  title?: string;
  align?: 'left' | 'right' | 'center';
  minWidth?: number;
  /** Enables sorting when provided. */
  sortValue?: (row: T) => number | string | null | undefined;
  render: (row: T) => ReactNode;
  /** Stick to the left edge while scrolling horizontally (first column). */
  sticky?: boolean;
  /** Stick to the right edge while scrolling horizontally (row actions). */
  stickyRight?: boolean;
  /** Renders as a row header (`th scope="row"`) in the heading font. */
  rowHeader?: boolean;
  /** The column the report is about (clicks, views): set off in the positive color. */
  emphasis?: boolean;
}

export type SortState = { key: string; dir: 'asc' | 'desc' } | null;

interface DataTableProps<T> {
  columns: DataColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string | number;
  loading?: boolean;
  error?: { message: string; onRetry?: () => void } | null;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  /** Cells keyed by column key for the highlighted total row. */
  totalRow?: Record<string, ReactNode>;
  onRowClick?: (row: T) => void;
  isRowSelected?: (row: T) => boolean;
  /** Controlled sorting; uncontrolled when omitted. */
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
  defaultSort?: SortState;
  minWidth?: number;
  /** Fixed-height scroll area with sticky header. */
  maxHeight?: number | string;
  /**
   * Accessible name for the table. It goes on the `<table>` itself rather than into a
   * visually hidden `<caption>`: an absolutely positioned caption is not sized by its own
   * `height` in Chrome, it takes the height of the table and stretches the page's scroll area,
   * which left a band of dead space below every long table.
   */
  caption?: string;
  skeletonRows?: number;
  /**
   * A refetch is in flight while rows are already on screen (a filter or page change). Shows a
   * thin progress bar instead of swapping the table for skeletons, so the layout does not jump.
   */
  busy?: boolean;
  /** Vertical lines between columns, for dense number grids (e.g. one column per month). */
  columnDividers?: boolean;
  /**
   * `classic` is the old dashboard's report table: sentence-case header on the panel color,
   * vertical lines between columns, compact rows, and a sort arrow only on the sorted column.
   */
  variant?: 'default' | 'classic';
  sx?: SxProps<Theme>;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export function sortRows<T>(rows: T[], columns: DataColumn<T>[], sort: SortState): T[] {
  if (!sort) return rows;
  const col = columns.find((c) => c.key === sort.key);
  if (!col?.sortValue) return rows;
  const get = col.sortValue;
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = get(a);
    const vb = get(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1; // empty values last, regardless of direction
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor;
    return collator.compare(String(va), String(vb)) * factor;
  });
}

/** Data table in the prototype style: uppercase header, sticky first column, total row. */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  loading,
  error,
  emptyTitle,
  emptyDescription,
  totalRow,
  onRowClick,
  isRowSelected,
  sort: controlledSort,
  onSortChange,
  defaultSort = null,
  minWidth,
  maxHeight,
  caption,
  skeletonRows = 6,
  busy,
  columnDividers,
  variant = 'default',
  sx,
}: DataTableProps<T>) {
  const classic = variant === 'classic';
  const { t } = useTranslation();
  const [innerSort, setInnerSort] = useState<SortState>(defaultSort);
  const sort = controlledSort !== undefined ? controlledSort : innerSort;
  const setSort = (next: SortState) => {
    if (controlledSort === undefined) setInnerSort(next);
    onSortChange?.(next);
  };

  const sorted = useMemo(() => sortRows(rows, columns, sort), [rows, columns, sort]);

  const toggleSort = (key: string) => {
    if (sort?.key !== key) setSort({ key, dir: 'asc' });
    else setSort({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
  };

  const stickySx =
    (bg: 'panel' | 'panel2' | 'primary050', edge: 'left' | 'right' = 'left') =>
    (theme: Theme) => ({
      position: 'sticky' as const,
      [edge]: 0,
      zIndex: 1,
      background: theme.vars.palette.md[bg],
      ...(edge === 'right' ? { boxShadow: `inset 1px 0 0 ${theme.vars.palette.md.border}` } : {}),
    });

  const showEmpty = !loading && !error && sorted.length === 0;
  // Never narrower than its columns need, so a hand-set `minWidth` can't fall behind them.
  const columnsWidth = columns.reduce((sum, col) => sum + (col.minWidth ?? 0), 0);
  const tableMinWidth = Math.max(minWidth ?? 0, columnsWidth) || undefined;

  return (
    <Box sx={sx}>
      <Box sx={{ position: 'relative' }}>
        <Fade in={!!busy && !loading} unmountOnExit>
          <LinearProgress
            aria-hidden
            sx={{ position: 'absolute', insetInline: 0, top: 0, height: 2, zIndex: 2 }}
          />
        </Fade>
      </Box>
      <Box sx={{ overflow: 'auto', maxHeight }}>
        <Table
          stickyHeader={!!maxHeight}
          sx={(theme) => ({
            minWidth: tableMinWidth,
            borderCollapse: 'separate',
            borderSpacing: 0,
            ...((columnDividers || classic) && {
              '& .MuiTableCell-root + .MuiTableCell-root': {
                borderLeft: `1px solid ${theme.vars.palette.md.border}`,
              },
            }),
          })}
          aria-busy={loading || busy || undefined}
          aria-label={caption}
        >
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                const active = sort?.key === col.key;
                const sortable = !!col.sortValue;
                const sortIcon = sortable && (!classic || active) && (
                  <Box
                    component="span"
                    sx={(theme) => ({
                      display: 'flex',
                      color: active ? theme.vars.palette.md.primary : theme.vars.palette.md.faint,
                      opacity: active ? 1 : 0.6,
                    })}
                  >
                    <Icon name={active && sort?.dir === 'desc' ? 'sortDown' : 'sortUp'} size={13} />
                  </Box>
                );
                const infoIcon = col.title && (
                  <Tooltip title={col.title}>
                    <Box component="span" sx={{ display: 'flex', opacity: 0.45 }}>
                      <Icon name="info" size={13} />
                    </Box>
                  </Tooltip>
                );
                // Icons go on the side away from the alignment edge, so the header text starts
                // (left) or ends (right) exactly where the column's values do.
                const right = col.align === 'right';
                const label = (
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    {right && sortIcon}
                    {right && infoIcon}
                    {col.header}
                    {!right && infoIcon}
                    {!right && sortIcon}
                  </Box>
                );
                return (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    sortDirection={active ? sort?.dir : false}
                    aria-sort={
                      active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    sx={[
                      { minWidth: col.minWidth },
                      col.sticky ? stickySx(classic ? 'panel' : 'panel2') : {},
                      col.sticky && !classic ? { pl: '18px', letterSpacing: '.09em' } : {},
                      classic
                        ? (theme: Theme) => ({
                            background: theme.vars.palette.md.panel,
                            color: theme.vars.palette.md.text,
                            fontSize: 14,
                            fontWeight: 500,
                            letterSpacing: 'normal',
                            textTransform: 'none',
                            py: 1,
                            px: 2,
                          })
                        : {},
                      col.stickyRight ? stickySx('panel2', 'right') : {},
                      // Header cells stay above body cells (sticky column and sticky header).
                      { zIndex: col.sticky || col.stickyRight ? 3 : 2 },
                    ]}
                  >
                    {sortable ? (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        sx={(theme) => ({
                          all: 'unset',
                          cursor: 'pointer',
                          borderRadius: radius.xs,
                          '&:hover': { color: theme.vars.palette.md.text },
                          '&:focus-visible': {
                            outline: `2px solid ${theme.vars.palette.md.primary}`,
                          },
                        })}
                      >
                        {label}
                      </Box>
                    ) : (
                      label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: skeletonRows }, (_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: placeholder rows
                <TableRow key={`sk-${i}`}>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={col.sticky ? stickySx('panel') : undefined}
                    >
                      <Skeleton width={col.sticky ? 110 : 64} sx={{ display: 'inline-block' }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!loading &&
              !error &&
              sorted.map((row) => {
                const selected = isRowSelected?.(row) ?? false;
                return (
                  <TableRow
                    key={getRowKey(row)}
                    hover
                    selected={selected}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onRowClick(row);
                            }
                          }
                        : undefined
                    }
                    sx={(theme) => ({
                      cursor: onRowClick ? 'pointer' : undefined,
                      '&.Mui-selected, &.Mui-selected:hover': {
                        background: theme.vars.palette.md.primary050,
                      },
                      '&:hover .md-sticky': { background: theme.vars.palette.md.panel2 },
                      '&:focus-visible': {
                        outline: `2px solid ${theme.vars.palette.md.primary}`,
                        outlineOffset: -2,
                      },
                    })}
                  >
                    {columns.map((col, i) => (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        component={col.rowHeader ? 'th' : 'td'}
                        scope={col.rowHeader ? 'row' : undefined}
                        className={col.sticky || col.stickyRight ? 'md-sticky' : undefined}
                        sx={[
                          { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
                          col.rowHeader && !classic
                            ? { fontFamily: fonts.head, fontWeight: 700, fontSize: 13.5 }
                            : {},
                          classic
                            ? (theme: Theme) => ({
                                py: 1.25,
                                px: 2,
                                color: theme.vars.palette.md[i === 0 ? 'text' : 'muted'],
                              })
                            : {},
                          col.emphasis ? { color: 'success.main', fontWeight: 500 } : {},
                          col.sticky ? stickySx(selected ? 'primary050' : 'panel') : {},
                          col.sticky && !classic ? { pl: '18px' } : {},
                          col.stickyRight
                            ? stickySx(selected ? 'primary050' : 'panel', 'right')
                            : {},
                        ]}
                      >
                        {col.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            {!loading && !error && sorted.length > 0 && totalRow && (
              <TableRow sx={(theme) => ({ background: theme.vars.palette.md.primary050 })}>
                {columns.map((col, i) => (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    component={i === 0 ? 'th' : 'td'}
                    scope={i === 0 ? 'row' : undefined}
                    sx={[
                      (theme) => ({
                        fontWeight: 800,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                        color: theme.vars.palette.md.primaryInk,
                        borderBottom: 0,
                        py: '14px',
                      }),
                      i === 0 ? { fontFamily: fonts.head, fontSize: 13.5 } : {},
                      col.sticky ? stickySx('primary050') : {},
                      col.sticky ? { pl: '18px' } : {},
                      col.stickyRight ? stickySx('primary050', 'right') : {},
                      // In a scroll area the totals stay in view at the bottom, like the header.
                      maxHeight
                        ? (theme: Theme) => ({
                            position: 'sticky',
                            bottom: 0,
                            zIndex: col.sticky || col.stickyRight ? 3 : 2,
                            background: theme.vars.palette.md.primary050,
                            boxShadow: `inset 0 1px 0 ${theme.vars.palette.md.border}`,
                          })
                        : {},
                    ]}
                  >
                    {totalRow[col.key] ?? ''}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {error && (
        <EmptyState
          tone="bad"
          icon="alert"
          title={t('states.errorTitle')}
          description={error.message}
          action={
            error.onRetry && (
              <Button
                size="small"
                onClick={error.onRetry}
                startIcon={<Icon name="repeat" size={16} />}
              >
                {t('actions.retry')}
              </Button>
            )
          }
        />
      )}
      {showEmpty && (
        <EmptyState
          icon="table"
          title={emptyTitle ?? t('states.empty')}
          description={emptyDescription}
        />
      )}
    </Box>
  );
}
